import React, {useEffect, useMemo, useState} from "react";
import { useParams } from "react-router-dom";
import ApiInterface from "@/commons/api/ApiInterface";
import Header from "@/commons/pages/borders/header/Header";
import Footer from "@/commons/pages/borders/footer/Footer";
import style from "../logchat.module.css";

import { Virtuoso } from 'react-virtuoso';
import { debounce } from 'lodash'

import { useNavigate } from 'react-router-dom';
import {loadFileInfos} from "@/admin/pages/logs/singlechat/utils/SingleLogUtils";
import styles from "@/admin/pages/logs/singlechat/logchat.module.css";
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";
import FloatingInput from "@/commons/components/fields/floatingInput/FloatingInput";

const minecraftColorCodes = {
    "0": "#000000",
    "1": "#0000AA",
    "2": "#00AA00",
    "3": "#00AAAA",
    "4": "#AA0000",
    "5": "#AA00AA",
    "6": "#FFAA00",
    "7": "#AAAAAA",
    "8": "#555555",
    "9": "#5555FF",
    "a": "#55FF55",
    "b": "#55FFFF",
    "c": "#FF5555",
    "d": "#FF55FF",
    "e": "#FFFF55",
    "f": "#FFFFFF",
    "l": "bold",
    "o": "italic",
    "n": "underline",
    "m": "line-through",
    "r": "reset"
};

const formatMinecraftColors = (text) => {
    if (typeof text !== "string") return null;

    const parts = text.split(/(&[0-9a-frlomn])/gi);
    const elements = [];
    let style = {};
    let keyCounter = 0;

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (part.startsWith('&')) {
            const code = part[1].toLowerCase();
            if (code === 'r') {
                style = {};
            } else if (minecraftColorCodes[code]) {
                if (["l", "o", "n", "m"].includes(code)) {
                    style = {
                        ...style,
                        fontWeight: code === 'l' ? 'bold' : style.fontWeight,
                        fontStyle: code === 'o' ? 'italic' : style.fontStyle,
                        textDecoration: code === 'n' ? 'underline' : code === 'm' ? 'line-through' : style.textDecoration
                    };
                } else {
                    style = { ...style, color: minecraftColorCodes[code] };
                }
            }
        } else {
            const withBreaks = part.split(/<br\s*\/?>/gi);
            for (let j = 0; j < withBreaks.length; j++) {
                elements.push(<span style={style} key={keyCounter++}>{withBreaks[j]}</span>);
                if (j < withBreaks.length - 1) {
                    elements.push(<br key={keyCounter++} />);
                }
            }
        }
    }
    return elements;
};

const SingleLogChatPublic = () => {
    const { uuid } = useParams();
    const type = "public";
    const [logLines, setLogLines] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [nameSearch, setNameSearch] = useState("");
    const [startTime, setStartTime] = useState("00:00:00");
    const [endTime, setEndTime] = useState("23:59:59");
    const [selectedModes, setSelectedModes] = useState([]);
    const [fileName, setFileName] = useState("");
    const [privateFile, setPrivateFile] = useState(null);

    const [prevFile, setPrevFile] = useState(null);
    const [nextFile, setNextFile] = useState(null);

    //Chats
    const [showStaffChat, setShowStaffChat] = useState(true);
    const [showPublicChat, setShowPublicChat] = useState(true);

    useEffect(() => {
        setLogLines([])
        loadFileInfos(uuid, setPrevFile, setNextFile, setFileName, setPrivateFile, "/logs/public/", "/logs/private/");
        ApiInterface.getFileUrl(uuid).then((url) => {
            fetch(url)
                .then(res => res.text())
                .then(text => {
                    const lines = text.split("\n").filter(Boolean);
                    setLogLines(lines);
                });
        });
    }, [uuid]);

    const debouncedSetSearchTerm = useMemo(() => debounce((val) => {
        setSearchTerm(val.toLowerCase());
    }, 200), []);

    const debouncedSetNameSearch = useMemo(() => debounce((val) => {
        setNameSearch(val.toLowerCase());
    }, 200), []);

    const handleSearch = (e) => {
        const value = e.target.value || "";
        debouncedSetSearchTerm(value);
    };

    const handleNameSearch = (e) => {
        const value = e.target.value || "";
        debouncedSetNameSearch(value);
    };

    const toggleChatType = (type) => {
        if (type === "staff") {
            const newState = !showStaffChat;
            // Se entrambi sarebbero disattivati, riattivali entrambi
            if (!newState && !showPublicChat) {
                setShowStaffChat(true);
                setShowPublicChat(true);
            } else {
                setShowStaffChat(newState);
            }
        } else if (type === "public") {
            const newState = !showPublicChat;
            if (!newState && !showStaffChat) {
                setShowStaffChat(true);
                setShowPublicChat(true);
            } else {
                setShowPublicChat(newState);
            }
        }
    };


    const toggleMode = (mode) => {
        setSelectedModes(prev =>
            prev.includes(mode)
                ? prev.filter(m => m !== mode)
                : [...prev, mode]
        );
    };

    const getFilteredLines = () => {
        return logLines.filter(line => {
            const timeMatch = line.match(/^(\d{2}:\d{2}:\d{2})/);
            const time = timeMatch ? timeMatch[1] : null;
            if (!time || time < startTime || time > endTime) return false;

            // StaffChat
            const staffChatMatch = line.match(/^(\d{2}:\d{2}:\d{2}) STAFFCHAT: (.+?)\[(.+?) \| (.+?)\] (.+)$/);
            if (staffChatMatch) {
                if (!showStaffChat) return false;

                const [, , whoRaw, mode, , content] = staffChatMatch;
                if (selectedModes.length && !selectedModes.includes(mode)) return false;
                if (nameSearch && !whoRaw.toLowerCase().includes(nameSearch)) return false;
                if (searchTerm && !content.toLowerCase().includes(searchTerm)) return false;
                return true;
            }

            // Annunci (Alert)
            const alertAnnounceMatch = line.match(/^(\d{2}:\d{2}:\d{2}) Alert: \[(.+?)\] -> (.+)$/);
            if (alertAnnounceMatch) {
                if (!showPublicChat) return false;

                const [, , mode, message] = alertAnnounceMatch;
                if (selectedModes.length && !selectedModes.includes(mode)) return false;
                if (nameSearch) return false; // gli alert non hanno un nome
                if (searchTerm && !message.toLowerCase().includes(searchTerm)) return false;
                return true;
            }

            // Saltare righe di tipo Alert senza formato
            if (line.startsWith("Alert:")) return false;

            // PublicChat
            const match = line.match(/^(\d{2}:\d{2}:\d{2}) (\w+): (.+?)\[(.+?) \| (.+?)\] (.+)$/);
            if (!match) return false;

            if (!showPublicChat) return false;

            const [, , , whoRaw, mode, , content] = match;
            if (selectedModes.length && !selectedModes.includes(mode)) return false;
            if (nameSearch && !whoRaw.toLowerCase().includes(nameSearch)) return false;
            if (searchTerm && !content.toLowerCase().includes(searchTerm)) return false;

            return true;
        });
    };

    const highlight = (text, term) => {
        if (!text) return null;
        if (!term) return formatMinecraftColors(text);
        const lower = text.toLowerCase();
        const index = lower.indexOf(term);
        if (index === -1) return formatMinecraftColors(text);

        const before = text.slice(0, index);
        const match = text.slice(index, index + term.length);
        const after = text.slice(index + term.length);
        return <>{formatMinecraftColors(before)}<mark>{match}</mark>{formatMinecraftColors(after)}</>;
    };

    const getAllModes = () => {
        const modes = new Set();logLines.forEach(line => {
            const match = line.match(/\[(.+?) \|/);
            if (match) {
                const mode = match[1];
                if (mode.toLowerCase() !== "default") modes.add(mode);
            }
            const alertMatch = line.match(/^\d{2}:\d{2}:\d{2} Alert: \[(.+?)\]/);
            if (alertMatch) {
                const mode = alertMatch[1];
                if (mode.toLowerCase() !== "default") modes.add(mode);
            }
        });

        // Aggiungi anche le modalità selezionate (forzatamente visibili)
        selectedModes.forEach(mode => {
            if (mode.toLowerCase() !== "default") modes.add(mode);
        });

        // Se è comunque vuoto, assicurati che ci sia almeno "default"
        if (modes.size === 0) {
            modes.add("default");
        }

        return Array.from(modes);
    };

    const renderLine = (line, i) => {
        // StaffChat
        const staffChatMatch = line.match(/^(\d{2}:\d{2}:\d{2}) STAFFCHAT: (.+?)\[(.+?) \| (.+?)\] (.+)$/);
        if (staffChatMatch) {
            const [, time, whoRaw, mode, , content] = staffChatMatch;
            return (
                <div key={i} className={style.logLinePrivate}>
                    <div className={style.time}>{time}</div>
                    <div className={style.mode}>{mode}</div>
                    <div className={style.fromto}>
                        <span style={{ color: "#ff4d4d", fontWeight: "bold" }}>StaffChat</span>
                        <span>{highlight(whoRaw, nameSearch)}</span>
                    </div>
                    <div className={style.text}>{highlight(content, searchTerm)}</div>
                </div>
            );
        }

        // Alert Announce
        const alertAnnounceMatch = line.match(/^(\d{2}:\d{2}:\d{2}) Alert: \[(.+?)\] -> (.+)$/);
        if (alertAnnounceMatch) {
            const [, time, mode, message] = alertAnnounceMatch;
            return (
                <div key={i} className={style.logLinePrivate} style={{ backgroundColor: "#322" }}>
                    <div className={style.time} style={{ backgroundColor: "#322" }}>{time}</div>
                    <div className={style.mode} style={{ backgroundColor: "#322" }}>{mode}</div>
                    <div className={style.fromto} style={{ backgroundColor: "#322" }}>Server</div>
                    <div className={style.text}>{highlight(message, searchTerm)}</div>
                </div>
            );
        }

        // PublicChat
        const match = line.match(/^(\d{2}:\d{2}:\d{2}) (\w+): (.+?)\[(.+?) \| (.+?)\] (.+)$/);
        if (!match) return null;

        const [, time, type, whoRaw, mode, world, content] = match;
        const who = type === "Alert" ? "Server" : whoRaw;
        const isEntry = content.includes("entra nel server");
        const isExit = content.includes("esce dal server");

        const rowStyle = isEntry
            ? { backgroundColor: "#1f2f1f", color: "#4caf50" }
            : isExit
                ? { backgroundColor: "#2a1a1a", color: "#f44336" }
                : {};

        return (
            <div key={i} className={style.logLinePrivate} style={rowStyle}>
                <div className={style.time} style={rowStyle}>{time}</div>
                <div className={style.mode} style={rowStyle}>{mode}</div>
                <div className={style.fromto} style={rowStyle}>{highlight(who, nameSearch)}</div>
                <div className={style.text} style={rowStyle}>{highlight(content, searchTerm)}</div>
            </div>
        );
    };

    const navigate = useNavigate();
    return (
        <>
            <div className="page__body">
                <Header/>
                <div className={"page__content"}>
                    <div className={styles.logNavbar}>
                        <CustomButton className={styles.button} onClick={() => window.location.href = "/logs/chats/public"}>
                            Vedi tutti i Log Pubblici
                        </CustomButton>
                        <CustomButton className={styles.button} onClick={() => window.location.href = "/logs/chat/private/"+privateFile}>
                            Passa al Log Privato
                        </CustomButton>
                    </div>
                    <div className={style.logNavbar}>
                        <CustomButton className={styles.inlineButton}>
                            {prevFile ? (
                                <CustomButton className={styles.inlineClickableButton} onClick={() => navigate("/logs/chat/public/"+prevFile)}>
                                    «
                                </CustomButton>
                            ) : (
                                <CustomButton className={styles.inlineNotClickableButton}>&nbsp;</CustomButton>
                            )}
                            Log del {fileName ? (<>{fileName}</>) : (<>XX.XX.XXXX</>)}
                            {nextFile ? (
                                <CustomButton className={styles.inlineClickableButton} onClick={() => navigate("/logs/chat/public/"+nextFile)}>
                                    »
                                </CustomButton>
                            ) : (
                                <CustomButton className={styles.inlineNotClickableButton}>&nbsp;</CustomButton>
                            )}
                        </CustomButton>
                    </div>

                    <div className={style.filtersBox}>
                        <div className={style.modeFilterContainer}>
                            <strong style={{ color: 'white', fontSize: '0.85rem' }}>Filtra modalità:</strong><br/><br/>
                            <div className={style.modeFilterBox}>
                                {getAllModes().map((mode) => (
                                    <label key={mode} className={style.checkboxLabelPlain}>
                                        <FloatingInput
                                            label={mode}
                                            type="checkbox"
                                            checked={selectedModes.includes(mode)}
                                            onChange={() => toggleMode(mode)}
                                        />
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className={style.modeFilterContainer}>
                            <strong style={{ color: 'white', fontSize: '0.85rem' }}>Filtra tipo di chat:</strong><br/><br/>
                            <div className={style.modeFilterBox}>
                                <label className={style.checkboxLabel}>
                                    <FloatingInput
                                        label={"Public Chat"}
                                        type="checkbox"
                                        checked={showPublicChat}
                                        onChange={() => toggleChatType("public")}
                                    />
                                </label>
                                <label className={style.checkboxLabel}>
                                    <FloatingInput
                                        label={"Staff Chat"}
                                        type="checkbox"
                                        checked={showStaffChat}
                                        onChange={() => toggleChatType("staff")}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className={style.timeFilters}>
                            <label>
                                Da:
                                <input type="time" value={startTime.slice(0, 5)} onChange={(e) => setStartTime(e.target.value + ":00")} />
                            </label>
                            <label>
                                A:
                                <input type="time" value={endTime.slice(0, 5)} onChange={(e) => setEndTime(e.target.value + ":00")} />
                            </label>
                        </div>
                        <FloatingInput
                            type="text"
                            className={style.searchInput}
                            label="Filtra per nome..."
                            onChange={handleNameSearch}
                        />
                        <FloatingInput
                            type="text"
                            className={style.searchInput}
                            label="Cerca nel contenuto..."
                            onChange={handleSearch}
                        />
                    </div>

                    <div className={style.logContainer}>
                        <Virtuoso
                            style={{ height: '600px', width: '100%' }}
                            className={style.virtuosoScroller}
                            totalCount={getFilteredLines().length}
                            itemContent={(index) => renderLine(getFilteredLines()[index], index)}
                        />
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};

export default SingleLogChatPublic;